import React, { useCallback, useEffect, useRef, useState } from 'react';
import Select from 'react-select'
import _ from 'lodash'

import './App.css';

const ENDPOINT_URL = 'https://us-central1-airtable-1519415217495.cloudfunctions.net/fetch-locations-for-position';

const OTHER_OFFICE_VAL = 0

declare global {
  interface Window { initVal?: string }
}


type Location = {
  city: string,
  state: string,
  zip: string
}

type BallotReadyOffice = {
  name: string,
  position_id: number
}

type JotFormSubmit = {
  valid: boolean,
  value: string
}

// JSON stringifed version of this is the "value" of this field
type SaveValue = {
  location: Location,
  selectedOffice?: number,
  officeName?: string
}

function App() {
  const [fullLocation, setFullLocation] = useState<Location>({
    city: '',
    state: '',
    zip: ''
  })
  const [officeOptions, setOfficeOptions] = useState<BallotReadyOffice[] | null>(null)
  const [selectedOffice, setSelectedOffice] = useState<number>()
  const [otherOffice, setOtherOffice] = useState('')
  const [loadingOffices, setLoadingOffices] = useState(false)
  const submitData = useRef<JotFormSubmit>({ valid: false, value: ''})

  

  // Helper to get the value that we save to JotForm
  const getSaveValue = () => {
    let officeName = otherOffice
    let selectedOfficeObject = null
    if (selectedOffice) {
      selectedOfficeObject = _.find(officeOptions, o => o.position_id === selectedOffice)
      if (selectedOfficeObject) {
        officeName = selectedOfficeObject.name
      }
    }
    return JSON.stringify({
      location: fullLocation,
      selectedOffice,
      officeName,
      officeData: selectedOfficeObject
    })
  }

  // Construct up-to-date submission value from
  submitData.current = {
    valid: !!(selectedOffice || otherOffice),
    value: getSaveValue()
  }

  // Callback for when user initiaties form submission
  const onFormSubmit = useCallback(() => {
    const result = submitData.current
    console.log('Form Submit. Saving result:', result)
    //@ts-expect-error
    window.JFCustomWidget.sendSubmit(result)
    return result
  }, [fullLocation, selectedOffice])

  // Init useEffect to attach submit callback to JotForm
  useEffect(() => {
    //@ts-expect-error
    JFCustomWidget.subscribe("submit", onFormSubmit)

    // If there's a pre-selected value, let's use it
    if (window.initVal) {
      const initVal: SaveValue = JSON.parse(window.initVal)
      console.log("InitVal", initVal)
      setFullLocation(initVal.location)
      setSelectedOffice(initVal.selectedOffice)
      setOtherOffice(initVal.officeName || '')
      // Fetch office options
      if (initVal.location?.city && initVal.location?.state && initVal.location?.zip) {
        retrieveOffices(initVal.location)
      }
    }
  }, [])

  // Any location input loses focus. We save data to Jotform
  const onLocationBlur = () => {
    console.log(fullLocation)
    if (fullLocation.city && fullLocation.state && fullLocation.zip) {
      retrieveOffices(fullLocation)
    }
    // Always send updated selected office
    // @ts-expect-error
    window.JFCustomWidget.sendData({ value: getSaveValue()})
  }

  // Hit up BallotReady like 'yo, what offices exist for the address at fullLocation'
  const retrieveOffices = useCallback(async (location: Location) => {
    setLoadingOffices(true)
    try {
      const response = await fetch(ENDPOINT_URL, {
        method: 'POST',
        body: JSON.stringify(location),
        headers: {
          "Content-Type": "application/json",
        }
      })
      const result = await response.json()
      setOfficeOptions(_.sortBy(result, 'name'))
      return result
    } catch {
      // This displays the Other... option so user can still type in an option
      setOfficeOptions([])
    } finally {
      setLoadingOffices(false)
    }
  }, [])

  // Whether or not we should show other office textbox. Only do this when BR doesn't return offices OR they choose other option from autocomplete
  // Because we set officeOptions when there's an error retrieving data from BR, we DO display office options when there is an error from BR
  const displayOtherOfficeOption = officeOptions?.length === 0 || selectedOffice === OTHER_OFFICE_VAL

  // Create array of office options. Always append Other office... so it's displayed even if we don't get data back from BallotReady
  const renderOptions = officeOptions ? officeOptions.map(o => ({label: o.name, value: o.position_id})) : []
  renderOptions.push({label: 'Other office...', value: OTHER_OFFICE_VAL})

  return (
    <div className="App">
      <h4>Your Location:*</h4>
      <div className="flex inputs-container">
        <div>
          <input onBlur={onLocationBlur} value={fullLocation.city} onChange={e => setFullLocation({ ...fullLocation, city: e.target.value })} name="city" />
          <label>City</label>
        </div>
        <div>
          <input onBlur={onLocationBlur} value={fullLocation.state} onChange={e => setFullLocation({ ...fullLocation, state: e.target.value })} name="state" />
          <label>State</label>
        </div>
        <div>
          <input type="number" maxLength={5} onBlur={onLocationBlur} value={fullLocation.zip} onChange={e => setFullLocation({ ...fullLocation, zip: e.target.value })} name="zip" />
          <label>Zip Code</label>
        </div>
      </div>
      <h4>Office Sought:*</h4>
      {loadingOffices && <div className="loader-container"><div className="loader"></div><p className='help'>Loading offices...</p></div>}
      {officeOptions === null && !loadingOffices && <p className='help'>Enter your location to see available offices...</p>}
      {officeOptions && !loadingOffices && (
        <div>
          <Select options={renderOptions} onChange={o => setSelectedOffice(o?.value ?? 0)} />
          <p className='help'>
            Offices shown are based on your selected location.<br/>
            Please select your office, or choose 'Other' if your office is not shown.
          </p>
        </div>
      )}
      {displayOtherOfficeOption && (
        <div className='other-office-container'>
          <input value={otherOffice} onChange={e => setOtherOffice(e.target.value)} name="other_office" /><br/>
          <label>Other Office Name</label>
          <p className='help'>Please enter using the format [Office Type, District # or Title, County/City, State Initials] ex. City Council, Ward 2, Rochester, NY</p>
        </div>
      )}
      {/* <p className='help version'>v1.1.0</p> */}
    </div>
  );
}

export default App;